import React, { useCallback, useEffect, useState } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Backdrop, CircularProgress } from '@mui/material';
import { Pick, Points, Team } from 'src/models';
import logging from 'src/services/logging';
import { query, collection, where, getDocs } from 'firebase/firestore';
import { db } from 'src/services/firebase-service';
import useUsers from 'src/hooks/useUsers';
import _ from 'lodash';
import useUser from 'src/hooks/useUser';

const columns = (
  topSeedAbbrev: string,
  botSeedAbbrev: string
): GridColDef[] => [
  { field: 'name', headerName: 'Name', width: 150 },
  {
    field: 'points',
    headerName: 'Points',
    width: 75,
    align: 'center',
  },
  {
    field: 'score',
    headerName: 'Score',
    width: 75,
    align: 'center',
  },
  {
    field: 'homePlayer',
    headerName: `${topSeedAbbrev} Player`,
    width: 150,
  },
  {
    field: 'awayPlayer',
    headerName: `${botSeedAbbrev} Player`,
    width: 150,
  },
  {
    field: 'numOfGames',
    headerName: 'Games',
    width: 75,
    align: 'center',
  },
  {
    field: 'winner',
    headerName: 'Winner',
    width: 160,
  },
];

type Props = {
  season: number;
  playoffSeriesLetter: string;
  topSeedTeam: Team;
  botSeedTeam: Team;
};

const PicksDataGrid = ({
  season,
  playoffSeriesLetter,
  topSeedTeam,
  botSeedTeam,
}: Props): JSX.Element => {
  const { fetchingUser, dbUser } = useUser();
  const { fetchingUsers, users } = useUsers();
  const [fetchingPicks, setFetchingPicks] = useState<boolean>(false);
  const [fetchingPoints, setFetchingPoints] = useState<boolean>(false);
  const [generatingRows, setGeneratingRows] = useState<boolean>(false);
  const [picks, setPicks] = useState<Pick[]>([]);
  const [points, setPoints] = useState<Points[]>([]);
  const [rows, setRows] = useState<any[]>([]);

  // TODO: can this be moved into ResultsAccordion to reduce reads?
  const fetchPicks = useCallback(async () => {
    try {
      logging.info(
        `Fetching picks for series ${playoffSeriesLetter}`,
        'PicksDataGrid'
      );
      setFetchingPicks(true);
      const q = query(
        collection(db, 'picks'),
        where('season', '==', season),
        where('playoffSeriesLetter', '==', playoffSeriesLetter)
      );
      const snapshot = await getDocs(q);
      const picks: Pick[] = [];
      snapshot.forEach((pick) => {
        picks.push(pick.data() as Pick);
      });
      setPicks(picks);
    } catch (error) {
      logging.error(error, 'PicksDataGrid');
    } finally {
      setFetchingPicks(false);
    }
  }, [playoffSeriesLetter, season]);

  useEffect(() => {
    fetchPicks();
  }, [fetchPicks]);

  const fetchPoints = useCallback(async () => {
    try {
      logging.info('Fetching points...', 'PicksDataGrid');
      setFetchingPoints(true);
      const q = query(
        collection(db, 'points'),
        where('season', '==', season),
        where('playoffSeriesLetter', '==', playoffSeriesLetter)
      );
      const snapshot = await getDocs(q);
      const points: Points[] = [];
      snapshot.forEach((point) => {
        points.push(point.data() as Points);
      });
      setPoints(points);
    } catch (error) {
      logging.error(error, 'PicksDataGrid');
    } finally {
      setFetchingPoints(false);
    }
  }, [playoffSeriesLetter, season]);

  useEffect(() => {
    fetchPoints();
  }, [fetchPoints]);

  useEffect(() => {
    const generateRows = () => {
      try {
        if (picks.length > 0 && users.length > 0 && dbUser) {
          logging.info(
            `Generating rows for series ${picks[0].playoffSeriesLetter}`,
            'PicksDataGrid'
          );
          setGeneratingRows(true);
          const _rows = picks.map((pick, index) => ({
            id: index,
            name: users.find((x) => x.uid === pick.uid)?.fullName,
            points: points
              .filter((x) => x.uid === pick.uid)
              .reduce((prev, curr) => prev + curr.total, 0),
            score: `${pick.score1} - ${pick.score2}`,
            homePlayer: pick.homePlayer.fullName,
            awayPlayer: pick.awayPlayer.fullName,
            numOfGames: pick.numOfGames,
            winner: pick.team.name,
            uid: pick.uid,
          }));
          const sortedRows = _.orderBy(_rows, ['name'], ['asc']);
          const indexOfCurrentUser = sortedRows.findIndex(
            (u) => u.uid === dbUser.uid
          );
          if (indexOfCurrentUser !== -1) {
            const currentUserRow = sortedRows.splice(indexOfCurrentUser, 1)[0];
            sortedRows.unshift(currentUserRow);
          }
          setRows(sortedRows);
          setGeneratingRows(false);
        }
      } catch (error) {
        logging.error(error, 'PicksDataGrid:generateRows');
      }
    };
    generateRows();
  }, [picks, points, users, dbUser]);

  if (fetchingPicks || fetchingUsers || fetchingPoints || fetchingUser) {
    return (
      <Backdrop
        open={fetchingPicks || fetchingUsers || fetchingPoints || fetchingUser}
      >
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  }

  return (
    <div style={{ height: 400, width: '100%' }}>
      <DataGrid
        density='compact'
        loading={generatingRows}
        rows={rows}
        columns={columns(topSeedTeam.abbreviation, botSeedTeam.abbreviation)}
        disableRowSelectionOnClick
        showCellVerticalBorder
        hideFooter
      />
    </div>
  );
};

export default PicksDataGrid;
